import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Send, Loader2, Sparkles, Tag, ArrowRight, Bot } from 'lucide-react';
import axios from 'axios';
import { ScrollArea } from './ui/scroll-area';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatPanel = ({ onClose, context, onTopicSelect, onKeywordSave }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your content brainstorming assistant. Tell me what kind of content you're looking to create, and I'll help you come up with viral ideas, topics, and keywords. What's on your mind?",
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setSuggestedTopics([]);
    setSuggestedKeywords([]);

    try {
      const response = await axios.post(`${API}/chat/brainstorm`, {
        message: userMessage,
        context: context,
        history: messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
      });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);

      if (response.data.suggested_topics?.length > 0) {
        setSuggestedTopics(response.data.suggested_topics);
      }
      if (response.data.suggested_keywords?.length > 0) {
        setSuggestedKeywords(response.data.suggested_keywords);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I'm having trouble connecting right now. Please try again in a moment." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Quick prompts
  const quickPrompts = [
    "Give me viral topic ideas",
    "What keywords should I use?",
    "How can I make this more engaging?",
    "Suggest a catchy hook",
  ];

  return (
    <motion.div
      className="fixed bottom-24 right-6 z-50 w-[400px] h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
      initial={{ scale: 0.9, opacity: 0, y: 20 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.9, opacity: 0, y: 20 }}
      data-testid="chat-panel"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-black to-[#7000FF] text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#CCFF00] flex items-center justify-center">
            <Bot className="w-5 h-5 text-black" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-sm">BRAINSTORM AI</h3>
            <p className="text-xs text-white/70">Content ideas assistant</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
          data-testid="chat-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Context indicator */}
      {(context?.audience || context?.therapeutic_area) && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 flex items-center gap-2 flex-wrap">
          <span className="font-medium">Context:</span>
          {context.audience && (
            <span className="px-2 py-0.5 bg-black text-white rounded-full">
              {context.audience}
            </span>
          )}
          {context.therapeutic_area && (
            <span className="px-2 py-0.5 bg-[#7000FF] text-white rounded-full">
              {context.therapeutic_area}
            </span>
          )}
          {context.disease && (
            <span className="px-2 py-0.5 bg-[#CCFF00] text-black rounded-full">
              {context.disease}
            </span>
          )}
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((msg, index) => (
            <motion.div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div
                className={`max-w-[85%] p-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-black text-white rounded-br-none'
                    : 'bg-gray-100 text-gray-800 rounded-bl-none'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </motion.div>
          ))}
          
          {isLoading && (
            <motion.div 
              className="flex justify-start"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="bg-gray-100 p-3 rounded-2xl rounded-bl-none">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#7000FF]" />
                  <span className="text-sm text-gray-600">Thinking...</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Suggested Topics */}
          {suggestedTopics.length > 0 && (
            <motion.div
              className="bg-[#CCFF00]/20 rounded-xl p-3 border border-[#CCFF00]"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> SUGGESTED TOPICS
              </p>
              <div className="space-y-1">
                {suggestedTopics.slice(0, 3).map((topic, i) => (
                  <button
                    key={i}
                    onClick={() => onTopicSelect(topic)}
                    className="w-full text-left text-sm p-2 rounded-lg bg-white hover:bg-[#CCFF00] transition-colors flex items-center justify-between group"
                  >
                    <span className="line-clamp-1">{topic}</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Suggested Keywords */}
          {suggestedKeywords.length > 0 && (
            <motion.div
              className="bg-[#7000FF]/10 rounded-xl p-3 border border-[#7000FF]/30"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1">
                <Tag className="w-3 h-3" /> SUGGESTED KEYWORDS
              </p>
              <div className="flex flex-wrap gap-1">
                {suggestedKeywords.map((kw, i) => (
                  <button
                    key={i}
                    onClick={() => onKeywordSave(kw)}
                    className="text-xs px-2 py-1 rounded-full bg-white hover:bg-[#7000FF] hover:text-white transition-colors border border-[#7000FF]/30"
                  >
                    + {kw}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Quick prompts */}
      {messages.length <= 2 && (
        <div className="px-4 py-2 border-t border-gray-100">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {quickPrompts.map((prompt, i) => (
              <button
                key={i}
                onClick={() => setInput(prompt)}
                className="flex-shrink-0 text-xs px-3 py-1.5 rounded-full bg-gray-100 hover:bg-[#CCFF00] transition-colors whitespace-nowrap"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about content ideas..."
            className="flex-1 px-4 py-2.5 rounded-full border border-gray-300 focus:border-[#7000FF] focus:ring-2 focus:ring-[#7000FF]/20 outline-none text-sm"
            disabled={isLoading}
            data-testid="chat-input"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:bg-[#7000FF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ChatPanel;
